import { SegmentedControl } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconPercentage } from "@tabler/icons-react";
import { t } from "i18next";
import round from "lodash/round";
import { useCallback, useMemo } from "react";

import { computationOptions } from "#app/computationOptions";
import { colors } from "#app/theme/v1/colors";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useUpdateQuoteRequestLineMargins } from "#modules/quoteRequestLines/view/useUpdateQuoteRequestLineMargins";
import { CostCollapsable } from "#modules/quoteRequestLines/views/DetailsPanel/CostCollapsable";
import { MarginsPrices } from "#modules/quoteRequestLines/views/DetailsPanel/Margins/MarginsPrices";
import { parseInputNumberValue, percentageOfTotal } from "#shared/helpers/prices";
import { Flex } from "#shared/view/components/Flex";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { computeQuoteRequestLinePrice } from "shared";

type MarginsSectionProps = {
  quoteRequestLine?: QuoteRequestLineDto;
};

const MarginsSection = ({ quoteRequestLine }: MarginsSectionProps) => {
  const [marginEditMode, setMarginEditMode] = useLocalStorage<{
    value: "margin" | "price";
  }>({
    key: "margin-edit-mode",
    defaultValue: { value: "margin" },
    serialize: (v) => JSON.stringify(v),
    deserialize: (v) => (v ? JSON.parse(v) : { value: "margin" }),
  });

  const { updateQuoteRequestLineMargins } = useUpdateQuoteRequestLineMargins();

  const canEditMargins = marginEditMode.value === "margin";
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestLine?.quoteRequestId);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestLine?.quoteRequestId);
  const qrlPrice = useMemo(
    () =>
      quoteRequestLine
        ? computeQuoteRequestLinePrice(
            {
              quoteRequestLine,
              optimizedMachineTimes,
              optimizedMaterialCosts,
            },
            computationOptions,
          )
        : null,
    [quoteRequestLine, optimizedMachineTimes, optimizedMaterialCosts],
  );

  const marginPercentage = percentageOfTotal(qrlPrice?.partMarginPrice, qrlPrice?.partCost, {
    rounded: false,
  });

  const materialMarginPercentage = quoteRequestLine?.materialMarginRatio ?? 0;
  const productMarginPercentage = quoteRequestLine?.productsMarginRatio ?? 0;
  const addedValueMarginPercentage = quoteRequestLine?.addedValueMarginRatio ?? 0;

  type MarginKey = "materialMarginRatio" | "productsMarginRatio" | "addedValueMarginRatio";

  const updateMargin = useCallback(
    (key: MarginKey, value: number) => {
      if (!quoteRequestLine?.id) {
        return;
      }

      const payload = { [key]: value } as Record<string, number>;
      updateQuoteRequestLineMargins({ id: quoteRequestLine.id, payload });
    },
    [quoteRequestLine?.id, updateQuoteRequestLineMargins],
  );

  const onMaterialMarginUpdate = useCallback(
    (value: number) => updateMargin("materialMarginRatio", value),
    [updateMargin],
  );

  const onProductMarginUpdate = useCallback(
    (value: number) => updateMargin("productsMarginRatio", value),
    [updateMargin],
  );

  const onAddedValueMarginUpdate = useCallback(
    (value: number) => updateMargin("addedValueMarginRatio", value),
    [updateMargin],
  );

  const onBlurHandler = (handler: (value: number) => void) => {
    return (e: React.FocusEvent<HTMLInputElement>) => {
      handler(parseInputNumberValue(e.target.value));
    };
  };

  const onMaterialBlur = onBlurHandler(onMaterialMarginUpdate);
  const onProductsBlur = onBlurHandler(onProductMarginUpdate);
  const onAddedValueBlur = onBlurHandler(onAddedValueMarginUpdate);

  return (
    <CostCollapsable
      title={t("qrl.margin.margins")}
      color={colors.violet[5]}
      value={round(marginPercentage, 2)}
      type="percentage"
      data-testid="margins-section"
    >
      <Flex direction="column" gap="xs">
        <SegmentedControl
          w="fit-content"
          key={marginEditMode.value}
          value={marginEditMode.value}
          onChange={(v) => setMarginEditMode({ value: v })}
          data={[
            {
              label: t("qrl.margin.editByMargin"),
              value: "margin",
            },
            {
              label: t("qrl.margin.editByPrice"),
              value: "price",
            },
          ]}
          size="sm"
        />
        <Flex gap="xs">
          <NumberInput
            label={t("qrl.materialGrade")}
            leftSection={<IconPercentage size={20} />}
            value={materialMarginPercentage}
            min={0}
            decimalScale={2}
            onBlur={onMaterialBlur}
            disabled={!canEditMargins}
          />
          <NumberInput
            label={t("qrl.margin.addedValue")}
            leftSection={<IconPercentage size={20} />}
            value={addedValueMarginPercentage}
            min={0}
            decimalScale={2}
            onBlur={onAddedValueBlur}
            disabled={!canEditMargins}
          />
          <NumberInput
            label={t("qrl.margin.productMargins")}
            leftSection={<IconPercentage size={20} />}
            value={productMarginPercentage}
            min={0}
            decimalScale={2}
            onBlur={onProductsBlur}
            disabled={!canEditMargins}
          />
        </Flex>
        <MarginsPrices
          qrlPrice={qrlPrice}
          onMaterialMarginUpdate={onMaterialMarginUpdate}
          onProductMarginUpdate={onProductMarginUpdate}
          onAddedValueMarginUpdate={onAddedValueMarginUpdate}
          disabled={canEditMargins}
        />
      </Flex>
    </CostCollapsable>
  );
};

export default MarginsSection;
